import { CarePathIcon, TableBody, TableCell, TableRow, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums/episodeCategory';
import { episodeCategoryIconName } from '@frontend/components/elements/Icons/episodeCategoryIconName';
import { generateUrlForRoute } from '@frontend/utils/hooks/useNavigateTo';
import type { IPatientTaskWorklist } from '../../../../../common/interfaces/patientTaskWorklist';
import type { IWorklistProcessTask } from '../../../../../common/interfaces/processes';
import type { IRequest } from '../../../../../common/interfaces/request';
import { WorkListItemType } from '../../../../../common/enums/workListItemType';
import { FullName } from '../../../elements/common';
import { DisplayDatePassed } from '../../../elements/DisplayDatePassed';
import { getServiceNameForWorkList } from '../../../elements/getServiceNameForWorkList';
import { NullableStringBirthDate } from '../../../elements/NullableStringBirthDate';
import { ProcessStatusLabel } from '../../../elements/processStatus/ProcessStatusLabel';
import { StatusLabelIcon } from '../../../elements/StatusLabelIcon';
import { StatusLabelIconPatientTask } from '../../../elements/StatusLabelIconPatientTask';

export interface IRequestsListBodyProps {
    requests: (IRequest | IPatientTaskWorklist | IWorklistProcessTask)[];
}

export function RequestsListBody(props: IRequestsListBodyProps) {
    const navigate = useNavigate();
    if (!props.requests.length || props.requests.length === 0) {
        return null;
    }

    const [tableBody, setTableBody] = useState<JSX.Element[]>([]);

    useEffect(() => {
        const body = props.requests.map((request) => {
            let url;
            let episodeCategory: EpisodeCategory;
            let serviceName;
            let canSeeRequesterAndCareProviderContext;
            let statusIcon;
            let lastVisited;
            let isTimeHidden;

            if (isWorklistRequest(request)) {
                url = `/episode?patientId=${request.patientId}&episodeCategory=${request.service.episodeCategory.name}&requestId=${request.id}`;
                episodeCategory = request.service.episodeCategory.name;
                serviceName = getServiceNameForWorkList(request.service.serviceType, request.service.code);
                canSeeRequesterAndCareProviderContext = request.authorization?.canSeeRequesterAndCareProviderContext;
                statusIcon = <StatusLabelIcon statusLabel={request.statusLabel} />;
                lastVisited = request.sortDate;
            } else if (isPatientTaskWorklist(request)) {
                url = `/contactsAndTasks?patientId=${request.patientId}&episodeCategory=${request.episodeCategory.name}&itemId=${request.id}`;
                episodeCategory = request.episodeCategory.name;
                serviceName = request.description;
                statusIcon = <StatusLabelIconPatientTask patientTask={request} />;
                canSeeRequesterAndCareProviderContext = true;
                lastVisited = request.updated_at;
                isTimeHidden = true;
            } else if (isWorklistProcessTask(request)) {
                url = generateUrlForRoute('patientCarePath', {
                    activeTab: 'carePath',
                    episodeCategory: request.service.episodeCategory.name,
                    patientId: request.patientId,
                });
                episodeCategory = request.service.episodeCategory.name;
                serviceName = getServiceNameForWorkList(request.service.serviceType, request.service.code);
                canSeeRequesterAndCareProviderContext = true;
                statusIcon = <ProcessStatusLabel {...request.statusLabelData} />;
                lastVisited = request.sortDate;
            } else {
                throw new Error('Worklist item is of an unknown taskType');
            }

            return (
                <TableRow
                    key={request.id}
                    onClick={() => navigate(url)}
                >
                    <TableCell>{statusIcon}</TableCell>
                    <TableCell>
                        <FullName {...request.patient} />
                    </TableCell>
                    <TableCell>
                        <NullableStringBirthDate value={request.patient?.birthDate} />
                    </TableCell>
                    <TableCell>
                        {episodeCategory && (
                            <CarePathIcon
                                icon={episodeCategoryIconName(episodeCategory)}
                                variant="small"
                            />
                        )}
                        <Text
                            as="span"
                            ml={2}
                            size="sm"
                        >
                            {serviceName}
                        </Text>
                    </TableCell>
                    <TableCell>
                        {canSeeRequesterAndCareProviderContext && request.patient && (
                            <>
                                <FullName {...request.startedByPractitioner} />
                                <br />
                                {request.startedByOrganization && (
                                    <Text
                                        as="span"
                                        color="minimal"
                                        size="sm"
                                    >
                                        {request.startedByOrganization.name}
                                    </Text>
                                )}
                            </>
                        )}
                    </TableCell>
                    <TableCell>
                        {canSeeRequesterAndCareProviderContext && request.patient && (
                            <>
                                <FullName {...request.patient.careProviderPractitioner} />
                                <br />
                                {request.patient.careProviderOrganization && (
                                    <Text
                                        as="span"
                                        color="minimal"
                                        size="sm"
                                    >
                                        {request.patient.careProviderOrganization.name}
                                    </Text>
                                )}
                            </>
                        )}
                    </TableCell>
                    <TableCell>
                        <DisplayDatePassed
                            isTimeHidden={isTimeHidden}
                            value={lastVisited}
                        />
                    </TableCell>
                </TableRow>
            );
        });
        setTableBody(body);
    }, [props.requests]);

    return <TableBody>{tableBody}</TableBody>;
}

function isWorklistRequest(item: IRequest | IPatientTaskWorklist | IWorklistProcessTask): item is IRequest {
    return item.taskType === WorkListItemType.request;
}

function isPatientTaskWorklist(
    item: IRequest | IPatientTaskWorklist | IWorklistProcessTask
): item is IPatientTaskWorklist {
    return item.taskType === WorkListItemType.patientTask;
}

function isWorklistProcessTask(
    item: IRequest | IPatientTaskWorklist | IWorklistProcessTask
): item is IWorklistProcessTask {
    return item.taskType === WorkListItemType.processTask;
}
